import React, { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
    SortingState,
    getSortedRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
} from '@tanstack/react-table';

import AuthLayout from '../components/AuthLayout';
import { Employee, useTenants } from '../hooks/useTenants';
import { AddEmployeeModal, EditEmployeeModal } from '../components/EmployeeModal';
import { storage } from '../utilities/storage';
import { useAuth } from '../hooks/useAuth';
import {
    AddressesColumn,
    ColumnsPagination,
    ColumnsSearching,
    ColumnsSorting,
    ColumnsVisibility,
    ExportReactTableToCSV,
} from '../components/TableFeatures';
import { deserializeHiddenColumns, toShortDate } from '../utilities/formatter';
import Loading from '../components/Loading';

const columnHelper = createColumnHelper<Employee>();

const tableColumnsSchema = [
    columnHelper.accessor('firstName', {
        id: 'First Name',
        cell: (info) => <div className="font-bold">{info.getValue()}</div>,
        header: (header) => (
            <div className="text-left uppercase">
                <ColumnsSorting header={header} />
            </div>
        ),
    }),
    columnHelper.accessor('lastName', {
        id: 'Last Name',
        cell: (info) => <div className="">{info.getValue()}</div>,
        header: (header) => (
            <div className="text-left uppercase">
                <ColumnsSorting header={header} />
            </div>
        ),
    }),
    columnHelper.accessor('email', {
        id: 'Email',
        cell: (info) => <div className="">{info.getValue()}</div>,
        header: (header) => (
            <div className="text-left uppercase">
                <ColumnsSorting header={header} />
            </div>
        ),
    }),
    columnHelper.accessor('addressesText', {
        id: 'Address',
        cell: ({ row }) => <AddressesColumn addresses={row.original.addresses || []} />,
        header: (header) => (
            <div className="text-left uppercase">
                <ColumnsSorting header={header} />
            </div>
        ),
    }),
    columnHelper.accessor('roleText', {
        id: 'Role',
        cell: (info) => <div className="">{info.getValue()}</div>,
        header: (header) => (
            <div className="text-left uppercase">
                <ColumnsSorting header={header} />
            </div>
        ),
    }),
    //TODO: Review what we should display here
    columnHelper.accessor('lastJobDate', {
        id: 'Last Job',
        cell: (info) => <div className="text-center">{toShortDate(info.getValue())}</div>,
        header: (header) => (
            <div className="text-center uppercase">
                <ColumnsSorting header={header} />
            </div>
        ),
        enableGlobalFilter: false,
    }),
    columnHelper.accessor('activeText', {
        id: 'Status',
        cell: (info) => (
            <div className="text-center">
                <div className="badge badge_outlined badge_secondary text-center uppercase">{info.getValue()}</div>
            </div>
        ),
        header: (header) => (
            <div className="text-center uppercase">
                <ColumnsSorting header={header} />
            </div>
        ),
    }),
    columnHelper.accessor('hourlyRateText', {
        id: 'Hourly Rate',
        cell: (info) => <div className="text-center">{info.getValue()}</div>,
        header: (header) => (
            <div className="text-center uppercase">
                <ColumnsSorting header={header} />
            </div>
        ),
    }),
    columnHelper.accessor('includeToSearch', {
        cell: (info) => info.getValue(),
    }),
];

const Employees = () => {
    const { user } = useAuth();

    const { useGetEmployees } = useTenants();
    const { data, isSuccess, isFetching } = useGetEmployees();

    const [filteredData, setFilteredData] = useState([]); 
    const [sorting, setSorting] = useState<SortingState>([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [columnVisibility, setColumnVisibility] = useState({});
    const [showInactiveEmployees, setShowInactiveEmployees] = useState(false);
    const fixedHiddenColumns = useMemo(() => ['includeToSearch', 'Last Job'], []);
    const initialHiddenColumns = useMemo(() => [], []);
    const storeTableVisibility = useMemo(
        () => storage<{}>(`workalert.state.employee_table_visibility.${user.id}`),
        [user.id]
    );

    const table = useReactTable({
        data: filteredData || [],
        columns: tableColumnsSchema,
        state: {
            sorting,
            globalFilter,
            columnVisibility,
        },
        initialState: {
            pagination: {
                pageIndex: 0,
                pageSize: 20,
            },
        },
        onSortingChange: setSorting,
        onGlobalFilterChange: setGlobalFilter,
        onColumnVisibilityChange: setColumnVisibility,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const searchHandler = (event: any) => {
        setGlobalFilter(event.target.value);
    };

    useEffect(() => {
        if (showInactiveEmployees)
            setFilteredData(data?.inactiveEmployees);
        else
            setFilteredData(data?.activeEmployees);
    }, [data, showInactiveEmployees]);

    useEffect(() => {
        if (columnVisibility && Object.keys(columnVisibility).length !== 0) {
            const tableVisibility = storeTableVisibility.get() || {};
            if (!_.isEqual(columnVisibility, tableVisibility)) {
                storeTableVisibility.set(columnVisibility);
            }
        }
    }, [columnVisibility, storeTableVisibility]);

    useEffect(() => {
        if (isSuccess) {
            let tableVisibility = storeTableVisibility.get() || {};
            if (Object.keys(tableVisibility).length === 0) {
                tableVisibility = deserializeHiddenColumns(fixedHiddenColumns.concat(initialHiddenColumns));
            }
            setColumnVisibility(tableVisibility);
        }
    }, [isSuccess, storeTableVisibility, fixedHiddenColumns, initialHiddenColumns]);

    return (
        <>
            <AuthLayout menuOption="Employees">
                <section className="breadcrumb lg:flex items-start">
                    <div>
                        <h1>Employees</h1>
                    </div>
                    <div className="flex flex-wrap gap-2 items-center ml-auto mt-5 lg:mt-0">
                        <div className="flex gap-x-2">
                                <label className="switch">
                                    <input
                                        type="checkbox"
                                        onChange={() => setShowInactiveEmployees(!showInactiveEmployees)}
                                    />
                                    <span></span>
                                    <span>Show inactive employees</span>
                                </label>
                        </div>
                        <div>
                            <ColumnsSearching
                                handler={searchHandler}
                                globalFilter={globalFilter}
                                onClear={() => {
                                    setGlobalFilter('');
                                }}
                            />
                        </div>
                        <div>
                            <AddEmployeeModal />
                        </div>
                    </div>
                </section>
                <div className="card p-5">
                    <div className="overflow-x-auto">
                        <Loading isLoading={isFetching} boxStyle />
                        <table className="table table-auto table_hoverable w-full">
                            <thead>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <tr key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <th key={header.id}>
                                                {header.isPlaceholder ? null : (
                                                    <div
                                                        className={`${
                                                            header.column.getCanSort()
                                                                ? 'cursor-pointer select-none'
                                                                : ''
                                                        }`}
                                                        onClick={header.column.getToggleSortingHandler()}
                                                    >
                                                        {flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                    </div>
                                                )}
                                            </th>
                                        ))}
                                        <th className="text-right">
                                            <ExportReactTableToCSV title={'Employees'} reacTable={table} />
                                            <ColumnsVisibility
                                                columns={table.getAllLeafColumns()}
                                                fixedHiddenColumns={fixedHiddenColumns}
                                            />
                                        </th>
                                    </tr>
                                ))}
                            </thead>
                            <tbody>
                                {isSuccess &&
                                    table.getRowModel().rows.map((row) => (
                                        <tr key={row.original.id}>
                                            {row.getVisibleCells().map((cell) => (
                                                <td key={cell.id}>
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                            ))}
                                            <td className="whitespace-nowrap">
                                                <div className="inline-flex ml-auto">
                                                    <EditEmployeeModal employeeId={row.original.id} />
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="card mt-5 lg:flex">
                    <ColumnsPagination table={table} onPageChanged={() => null} />
                </div>
            </AuthLayout>
        </>
    );
};

export default Employees;
